import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getRedirectUri, takePkceVerifier, takeReturnTo } from "@/integrations/spotify/client";
import { Loader2 } from "lucide-react";

export default function SpotifyCallback() {
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const errParam = params.get("error");
      const returnTo = takeReturnTo() || "/dashboard";

      if (errParam) {
        setError(errParam);
        return;
      }
      const verifier = takePkceVerifier();
      if (!code || !verifier) {
        setError("Missing authorization code or verifier.");
        return;
      }

      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setError("You must be signed in.");
        return;
      }

      const { error: fnErr } = await supabase.functions.invoke("spotify-token-exchange", {
        body: { code, code_verifier: verifier, redirect_uri: getRedirectUri() },
      });

      if (fnErr) {
        setError(fnErr.message);
        return;
      }

      navigate(returnTo, { replace: true });
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="rounded-3xl backdrop-blur-2xl bg-white/5 border border-white/10 p-8 max-w-md w-full text-center">
        {error ? (
          <>
            <h1 className="text-xl font-medium mb-2">Spotify connection failed</h1>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            >
              Back to dashboard
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Connecting your Spotify account…</p>
          </>
        )}
      </div>
    </div>
  );
}
